import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ControlError } from '../../models';
import { FileInputType } from '../../enums';
import { filesSizeValidator, filesTypeValidator } from '../../utils/form.validators';
import { LayoutUtility } from '../../utils/layout.utility';

@Component({
  selector: 'kt-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss'],
})
export class FileInputComponent implements OnInit, AfterViewInit {
  filesControl = new FormControl([]);
  namesControl = new FormControl('');
  errors: ControlError[] = [];
  loading = false;
  isDialog = false;
  fileOver = false;
  @Input() multiple: boolean;
  @Input() type: FileInputType;
  @Input() maxSize: number; // size in MBs
  @Input() browseLabel = 'Browse files';
  @Input() submitLabel = 'Upload';
  @Input() text = 'Drag and drop a file or';
  @Input() color: 'primary' | 'secondary' | 'tertiary' | 'neutral' = 'neutral';
  @Output() submitted = new EventEmitter<File | File[]>();

  constructor(
    private layoutUtility: LayoutUtility,
    @Optional() private dialogRef?: MatDialogRef<FileInputComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private data?,
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.initFields();

    const validators = [Validators.required];
    this.errors.push({ validation: 'required', message: 'Please select a file' });

    if (this.type) {
      validators.push(filesTypeValidator(this.filesControl, this.type));
      this.errors.push({
        validation: 'filesType',
        message: `Please select valid file/s, accepted type "${this.type}"`,
      });
    }

    if (this.maxSize) {
      validators.push(filesSizeValidator(this.filesControl, this.maxSize));
      this.errors.push({
        validation: 'filesSize',
        message: `Please select valid file/s, max accepted size "${this.maxSize}MB"`,
      });
    }

    this.namesControl.setValidators(validators);
  }

  ngAfterViewInit(): void {
    setTimeout(() => (this.loading = false));
  }

  initFields(): void {
    this.isDialog = this.data.isDialog ?? this.isDialog;
    this.multiple = this.data.multiple ?? this.multiple;
    this.type = this.data.type ?? this.type;
    this.maxSize = this.data.maxSize ?? this.maxSize;
    this.browseLabel = this.data.browseLabel ?? this.browseLabel;
    this.submitLabel = this.data.submitLabel ?? this.submitLabel;
    this.text = this.data.text ?? this.text;
    this.color = this.data.color ?? this.color;
  }

  stopEvent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  handleFiles(event: Event): void {
    this.stopEvent(event);

    let files: File[];

    if (event instanceof DragEvent) {
      const dataTransfer = event.dataTransfer;

      files = dataTransfer.files?.length > 0 ? Array.from(dataTransfer.files) : [];
    } else {
      const input = event.target as HTMLInputElement;

      files = input.files?.length > 0 ? Array.from(input.files) : [];
    }

    this.filesControl.setValue(files);
    this.namesControl.setValue(files.map((f) => f.name).join(', '));
    this.namesControl.markAsTouched();
  }

  close(files: File | File[] = null): void {
    this.dialogRef.close(files);
  }

  submit(): void {
    this.namesControl.markAsTouched();

    if (this.namesControl.invalid) {
      this.layoutUtility.showActionNotification('ERROR', 'To continue, select valid file/s');

      return;
    }

    let files = this.filesControl.value;
    files = files.length ? (this.multiple ? files : files[0]) : null;

    this.filesControl.reset();
    this.namesControl.reset();

    if (this.isDialog) {
      this.close(files);
    } else {
      this.submitted.emit(files);
    }
  }
}
